import { Box, Flex, FlexProps, Icon, List, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import { FC, useState } from 'react';
import { InView } from 'react-intersection-observer';

import CheckMarkIcon from '@/assets/icons/ic-check-mark.svg';

import { FilterComboboxItem } from '@/components/ui/features/hubs-speakers/SpeakersFilter/components/FilterComboboxItem';
import { FilterComboboxInput } from '@/components/ui/features/library/LibraryFilter/components/FilterComboboxInput';
import { IAlgoliaFilterItem } from '@/interfaces/IAlgoliaFilterItem';

interface ICombobox extends FlexProps {
  itemsList: Array<IAlgoliaFilterItem>;
  placeholder?: string;
  isLoadingMore?: boolean;
  defaultItem: IAlgoliaFilterItem;
  onScrollToBottom?: () => void;
  onChangeItem(item: IAlgoliaFilterItem): void;
  onSearchChange(inputValue: string): void;
}

export const FilterCombobox: FC<ICombobox> = ({
  onChangeItem,
  defaultItem,
  onSearchChange,
  onScrollToBottom,
  placeholder,
  isLoadingMore,
  itemsList,
}) => {
  const [selectedValue, setSelectedValue] = useState(defaultItem.value || null);
  const itemsWithoutSelected = itemsList.filter((it) => it.value !== selectedValue);
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    selectedItem = defaultItem,
    setInputValue,
    getItemProps,
  } = useCombobox({
    isOpen: true,
    items: itemsWithoutSelected,
    itemToString(item) {
      return item ? item.label : '';
    },
    onInputValueChange: ({ inputValue }) => {
      onSearchChange(inputValue);
    },
    onSelectedItemChange: ({ selectedItem: item }) => {
      setInputValue('');
      onChangeItem(item);
      setSelectedValue(item.value);
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          return {
            ...changes,
            highlightedIndex: 0,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.InputFocus:
          return {
            ...changes,
            highlightedIndex: 0,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
  });

  const selected = selectedItem || defaultItem;

  return (
    <Flex direction="column" flex="1 1 auto" borderRadius="2px">
      <Flex direction="row" alignItems="baseline">
        <FilterComboboxInput {...getInputProps()} placeholder={placeholder} />
      </Flex>
      {selected && (
        <Flex w="100%" my="3" justify="space-between">
          <Text fontWeight="semibold">{selected?.label}</Text>
          <Box>
            <Icon as={CheckMarkIcon} color="violet.400" ml="auto" />
          </Box>
        </Flex>
      )}
      <List display={isOpen ? 'block' : 'none'} {...getMenuProps()} flex={1} overflowY="auto" maxH="415">
        {itemsWithoutSelected.map((item, index) => {
          return (
            <FilterComboboxItem
              {...getItemProps({ item, index })}
              key={item.value}
              as="li"
              item={item}
              isHighlighted={index === highlightedIndex}
            />
          );
        })}
        {isLoadingMore ? <ItemLoadingFallback /> : null}
        <InView
          as="span"
          skip={!onScrollToBottom}
          rootMargin="20px 0px 0px 0px"
          onChange={(inView) => inView && onScrollToBottom?.()}
        />
      </List>
    </Flex>
  );
};

function ItemLoadingFallback() {
  return (
    <VStack spacing={6}>
      <Box px={4} w="full">
        <Skeleton h={6} w="full" />
      </Box>
      <Box px={4} w="full">
        <Skeleton h={6} w="full" />
      </Box>
      <Box px={4} w="full">
        <Skeleton h={6} w="full" />
      </Box>
    </VStack>
  );
}
